import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import Logo from "@/components/Logo";
import {
  ScanLine, PackageOpen, ListChecks, History, LogOut, Menu, X, Package as PackageIcon, ChevronRight,
} from "lucide-react";

const TABS = [
  { to: "/dispatcher", end: true, icon: ScanLine, label: "Scan" },
  { to: "/dispatcher/for-pickup", icon: PackageOpen, label: "For Pick-Up" },
  { to: "/dispatcher/processed", icon: ListChecks, label: "Processed" },
  { to: "/dispatcher/history", icon: History, label: "History" },
];

export default function DispatcherLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white flex flex-col">
      <header className="sticky top-0 z-30 glass border-b border-slate-200 dark:border-slate-800 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo className="h-10" />
          <div className="leading-tight border-l border-slate-200 dark:border-slate-800 pl-3">
            <div className="text-[10px] font-mono-tech uppercase tracking-wider text-cyan-400">
              Dispatcher · {user?.store_name || "—"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle size="sm" />
          <button
            data-testid="disp-menu-btn"
            onClick={() => setMenuOpen(true)}
            className="text-slate-700 dark:text-slate-300 hover:text-slate-900 hover:dark:text-white"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 px-4 py-5 pb-24 max-w-3xl mx-auto w-full">
        <Outlet />
      </main>

      {/* Bottom tab nav (mobile-first) */}
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-white/95 dark:bg-slate-950/95 backdrop-blur border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-3xl mx-auto grid grid-cols-4">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              data-testid={`disp-tab-${tab.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) =>
                [
                  "flex flex-col items-center gap-1 py-2.5 text-[10px] font-mono-tech uppercase tracking-wider transition-colors",
                  isActive ? "text-cyan-400" : "text-slate-500 hover:text-slate-900 hover:dark:text-white",
                ].join(" ")
              }
            >
              {({ isActive }) => (
                <>
                  <tab.icon className={`w-5 h-5 ${isActive ? "text-cyan-400" : ""}`} />
                  <span>{tab.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Slide-in menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMenuOpen(false)} />
          <div className="absolute inset-y-0 right-0 w-72 bg-white dark:bg-slate-950 border-l border-slate-200 dark:border-slate-800 p-5 flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <span className="font-display font-bold">Menu</span>
              <button onClick={() => setMenuOpen(false)} className="text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-3 py-3 rounded-md bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 mb-5">
              <div className="text-xs text-slate-500 font-mono-tech uppercase tracking-wider">Logged in as</div>
              <div className="font-display font-semibold mt-1">{user?.name}</div>
              <div className="text-xs text-slate-600 dark:text-slate-400">{user?.email}</div>
              <div className="text-[10px] mt-2 font-mono-tech uppercase tracking-wider text-cyan-400">
                {user?.position || "Dispatcher"} · {user?.store_name}
              </div>
            </div>
            <ul className="space-y-1 flex-1">
              {TABS.map(tab => (
                <li key={tab.to}>
                  <NavLink
                    to={tab.to}
                    end={tab.end}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      [
                        "flex items-center justify-between px-3 py-2.5 rounded-md text-sm transition-colors",
                        isActive ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/30"
                                 : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 hover:dark:bg-slate-900 border border-transparent",
                      ].join(" ")
                    }
                  >
                    <span className="flex items-center gap-3"><tab.icon className="w-4 h-4" /> {tab.label}</span>
                    <ChevronRight className="w-4 h-4 text-slate-500 dark:text-slate-600" />
                  </NavLink>
                </li>
              ))}
            </ul>
            <button
              data-testid="disp-logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-md mt-4"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
